/**
 * useComponentTiming — a block reports its own "data ready" time to the
 * enclosing PageTimingProvider. No-ops when no provider is mounted (e.g. a card
 * reused on a page without timing), so it's safe to call unconditionally.
 *
 * `settled` is derived by the block from its own query state
 * (e.g. `!isLoading`, or `queries.every(q => !q.isLoading)`).
 *
 * Design: docs/FeatureDesigns/page-load-perf-drilldown.md
 */

import { useEffect, useRef } from 'react';
import { usePageTimingReport } from './pageTimingContext';

export function useComponentTiming(name: string, settled: boolean): void {
  const { report, active } = usePageTimingReport();
  const startRef = useRef(performance.now());
  const sawLoadingRef = useRef(false);
  const reportedRef = useRef(false);

  // Track (during render) whether we ever saw an unsettled state — if not, the
  // block resolved straight from cache.
  if (!settled) sawLoadingRef.current = true;

  useEffect(() => {
    if (!active || reportedRef.current || !settled) return;
    reportedRef.current = true;
    report(name, Math.round(performance.now() - startRef.current), !sawLoadingRef.current);
  }, [active, settled, name, report]);
}
